/**
 * Back-off for background polls that keep getting a 4xx.
 *
 * A 4xx on a poll is almost always about this user (no access, or a resource
 * that doesn't exist for them), and it won't fix itself in 15 or 30 seconds.
 * Polling on regardless logs a failed request on every tick, all day, for every
 * open add-in, and that buries real failures on the Logs page and in Workspace
 * Health.
 *
 * After {@link POLL_BACKOFF_AFTER_FAILURES} consecutive 4xx responses, timer
 * ticks poll at most once every {@link POLL_BACKOFF_INTERVAL_MS}. Any success
 * resets it. Callers consult {@link PollBackoff.shouldSkip} only for timer
 * ticks, so a refresh the user asks for always runs.
 *
 * 5xx and network failures break the streak instead of extending it. Those are
 * usually transient, and the normal interval notices recovery soonest.
 */

export const POLL_BACKOFF_AFTER_FAILURES = 3;
export const POLL_BACKOFF_INTERVAL_MS = 5 * 60_000;

/**
 * An HTTP failure that keeps its status code, so a poller can tell a 4xx from
 * a 5xx. `name` is left as "Error" on purpose: connector failure telemetry
 * reports the error's name as its type, and that label should not change.
 */
export class HttpStatusError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/** True for an {@link HttpStatusError} carrying a 4xx status. */
export function isClientErrorStatus(error: unknown): boolean {
  return error instanceof HttpStatusError && error.status >= 400 && error.status < 500;
}

export interface PollBackoff {
  /** True while a timer tick should skip its poll. */
  shouldSkip(now?: number): boolean;
  /** Record a poll that succeeded. */
  recordSuccess(): void;
  /** Record a poll that failed with `error`. */
  recordFailure(error: unknown, now?: number): void;
}

export function createPollBackoff(): PollBackoff {
  let consecutiveClientErrors = 0;
  let resumeAt = 0;

  const reset = (): void => {
    consecutiveClientErrors = 0;
    resumeAt = 0;
  };

  return {
    shouldSkip: (now = Date.now()) => now < resumeAt,
    recordSuccess: reset,
    recordFailure: (error, now = Date.now()) => {
      if (!isClientErrorStatus(error)) {
        reset();
        return;
      }
      consecutiveClientErrors += 1;
      if (consecutiveClientErrors >= POLL_BACKOFF_AFTER_FAILURES) {
        resumeAt = now + POLL_BACKOFF_INTERVAL_MS;
      }
    },
  };
}
